/**
 * Keranjang belanja & wishlist.
 *
 * Disimpan di localStorage, bukan di server: keranjang bukan data yang perlu
 * bertahan lintas perangkat, dan menyimpannya di server berarti satu tabel
 * tambahan plus sinkronisasi yang tidak sepadan untuk toko sekecil ini.
 *
 * Harga di sini TIDAK dipercaya. Server menghitung ulang total dari harga
 * varian saat checkout — angka di keranjang murni untuk ditampilkan.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface CartLine {
  variant_id: string;
  product_slug: string;
  product_name: string;
  variant_name: string;
  unit_price: number;
  quantity: number;
  delivery_type: string;
  image?: string;
}

/** Item wishlist disimpan per produk, bukan per varian. */
export interface WishlistItem {
  product_slug: string;
  product_name: string;
  image?: string;
}

const CART_KEY = "ag5r:cart";
const WISHLIST_KEY = "ag5r:wishlist";
const MAX_QTY = 10;

function readStore<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    // localStorage bisa diblokir (mode privat) atau isinya korup — jangan
    // sampai itu membuat seluruh halaman gagal render.
    return [];
  }
}

function writeStore(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Kuota penuh atau storage diblokir — keranjang tetap jalan di memori.
  }
}

interface CartContextValue {
  lines: CartLine[];
  wishlist: WishlistItem[];
  count: number;
  subtotal: number;
  addLine: (line: Omit<CartLine, "quantity">, quantity?: number) => void;
  setQuantity: (variantId: string, quantity: number) => void;
  removeLine: (variantId: string) => void;
  clear: () => void;
  toggleWishlist: (item: WishlistItem) => void;
  isWishlisted: (slug: string) => boolean;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => readStore<CartLine>(CART_KEY));
  const [wishlist, setWishlist] = useState<WishlistItem[]>(() =>
    readStore<WishlistItem>(WISHLIST_KEY),
  );

  useEffect(() => writeStore(CART_KEY, lines), [lines]);
  useEffect(() => writeStore(WISHLIST_KEY, wishlist), [wishlist]);

  const addLine = useCallback((line: Omit<CartLine, "quantity">, quantity = 1) => {
    setLines((prev) => {
      const existing = prev.find((l) => l.variant_id === line.variant_id);
      if (!existing) return [...prev, { ...line, quantity: Math.min(quantity, MAX_QTY) }];
      // Varian sama ditambah lagi → naikkan jumlah, jangan duplikat baris.
      return prev.map((l) =>
        l.variant_id === line.variant_id
          ? { ...l, quantity: Math.min(l.quantity + quantity, MAX_QTY) }
          : l,
      );
    });
  }, []);

  const setQuantity = useCallback((variantId: string, quantity: number) => {
    setLines((prev) =>
      quantity < 1
        ? prev.filter((l) => l.variant_id !== variantId)
        : prev.map((l) =>
            l.variant_id === variantId ? { ...l, quantity: Math.min(quantity, MAX_QTY) } : l,
          ),
    );
  }, []);

  const removeLine = useCallback((variantId: string) => {
    setLines((prev) => prev.filter((l) => l.variant_id !== variantId));
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const toggleWishlist = useCallback((item: WishlistItem) => {
    setWishlist((prev) =>
      prev.some((w) => w.product_slug === item.product_slug)
        ? prev.filter((w) => w.product_slug !== item.product_slug)
        : [...prev, item],
    );
  }, []);

  const value = useMemo<CartContextValue>(() => {
    const count = lines.reduce((n, l) => n + l.quantity, 0);
    const subtotal = lines.reduce((n, l) => n + l.unit_price * l.quantity, 0);
    return {
      lines,
      wishlist,
      count,
      subtotal,
      addLine,
      setQuantity,
      removeLine,
      clear,
      toggleWishlist,
      isWishlisted: (slug: string) => wishlist.some((w) => w.product_slug === slug),
    };
  }, [lines, wishlist, addLine, setQuantity, removeLine, clear, toggleWishlist]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
